import { useState } from 'react';
import { Button, Dropdown, message, Space, Tabs, Typography } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { SvgIcon } from 'src/components/icon';
import { downloadSessions, getAllSessions } from '@/api/admin';
import { ReviewModal } from '@/components/modals/ReviewBooking';
import Reviews from './TablePage';
import { canEditReview } from './canEditReview';
import { Booking, Session } from '@/types';
import { ColumnsType } from 'antd/es/table';
import DateRangeFilter from '@/components/admin/payments/DateRangeFilter';
import './index.scss';

type RatingTab = 'franchisors' | 'consultants';

interface ReviewItem {
  rating?: number;
  comment?: string;
  ratings?: Array<{ questionId: string; rating: number }>;
}

interface ReviewRow {
  _id: string;
  createdAt?: string;
  franchise?: {
    company?: string;
  };
  consultant?: {
    firstName?: string;
    lastName?: string;
  };
  review?: ReviewItem[];
  status?: string;
}

// Figma shows the table Date column as "9 Mar, 2024, 1:00 PM" — keep the
// time component so admins can disambiguate same-day sessions.
const formatDateTime = (iso?: string) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  const date = d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
  const time = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${date}, ${time}`;
};

const renderStatus = (status?: string) => {
  let statusColor = '#1C8271';
  let statusText = 'Activated';
  if (status === 'future') {
    statusColor = '#4f67bcff';
    statusText = 'Future';
  } else if (status === 'pending') {
    statusColor = '#F6CB0B';
    statusText = 'Pending';
  } else if (status === 'completed') {
    statusColor = '#1C8271';
    statusText = 'Completed';
  } else if (status === 'declined') {
    statusColor = '#F68B8B';
    statusText = 'Declined';
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: statusColor,
        }}
      />
      <Typography.Text>{statusText}</Typography.Text>
    </div>
  );
};

const renderQuestionTitle = (label: string) => (
  <span className={'rating-management-q-title'}>
    {label}
    <SvgIcon type="goldStar" width={14} height={14} />
  </span>
);

export default function AdminReviews() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState({} as Booking);
  const [isViewOrEdit, setIsViewOrEdit] = useState('view');

  const activeTab: RatingTab =
    (searchParams.get('tab') as RatingTab) || 'franchisors';

  const handleTabChange = (next: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('tab', next);
      return params;
    });
    setPage(1);
  };

  // The Date filter writes startDate / endDate URL params. On the backend,
  // GetFilteredSessionsAdmin currently filters session.createdAt (when the
  // booking was created). This is the same semantic used by TCX-550's
  // Payments Management. If product wants "review submission date" or
  // "session start date" instead, switch the BE query to review.createdAt
  // or session.start respectively — flagged for George's confirmation in
  // the PR comments.
  const handleDateRangeChange = (from: string, to: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (from) next.set('startDate', from);
      else next.delete('startDate');
      if (to) next.set('endDate', to);
      else next.delete('endDate');
      return next;
    });
    setPage(1);
  };

  const { data, refetch } = useQuery<
    {
      total: number;
      page: number;
      limit: number;
      sessions: Session[];
    },
    Error
  >({
    queryKey: ['sessions', searchParams.toString(), page, limit],
    queryFn: () =>
      getAllSessions(
        {
          status: searchParams.get('status') || undefined,
          search: searchParams.get('search') || undefined,
          startDate: searchParams.get('startDate') || undefined,
          endDate: searchParams.get('endDate') || undefined,
          // Scope reviews to the active tab's direction so review[0] resolves
          // to the correct review (Franchisors → consultant→franchisor with
          // Q1/Q2/Q3; Consultants → franchisor→consultant single rating).
          tab: activeTab,
        },
        { page, limit },
      ),
  });

  const handleModalOpen = (booking: Booking, type: string) => {
    setIsViewOrEdit(type);
    setSelectedBooking(booking);
    setIsModalOpen(true);
  };

  const handleModalClose = async () => {
    setIsModalOpen(false);
    setSelectedBooking({} as Booking);
    await refetch();
  };

  // CSV export — hits /admin/sessions/download with the same filters as the
  // current view (status / search / date range) plus the active tab so the
  // projected columns match what's on screen.
  const { mutateAsync: triggerDownload, isPending: isDownloading } = useMutation(
    {
      mutationFn: () =>
        downloadSessions({
          search: searchParams.get('search') || undefined,
          status: searchParams.get('status') || undefined,
          startDate: searchParams.get('startDate') || undefined,
          endDate: searchParams.get('endDate') || undefined,
          tab: activeTab,
        }),
      onError: (error: any) => {
        message.error(error?.message ?? 'Failed to download table');
      },
    },
  );

  const handleDownloadTable = async () => {
    try {
      const { file } = await triggerDownload();
      if (!file) throw new Error('Empty file from server');
      const csv = atob(file);
      const blob = new Blob([csv], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `rating-management-${activeTab}-${new Date().toISOString()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      message.success('Download started');
    } catch (e: any) {
      message.error(e?.message ?? 'Failed to download table');
    }
  };

  const idColumn: ColumnsType<ReviewRow>[number] = {
    title: 'ID',
    dataIndex: '_id',
    key: '_id',
    render: (text: string) => <span style={{ fontWeight: 500 }}>{text}</span>,
  };

  const dateColumn: ColumnsType<ReviewRow>[number] = {
    title: 'Date',
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: (date: string) => formatDateTime(date),
  };

  const statusColumn: ColumnsType<ReviewRow>[number] = {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: renderStatus,
  };

  const franchisorColumn: ColumnsType<ReviewRow>[number] = {
    title: 'Franchisor',
    dataIndex: 'franchise',
    key: 'franchise',
    render: (franchise) => franchise?.company || 'N/A',
  };

  const consultantColumn: ColumnsType<ReviewRow>[number] = {
    title: 'Consultant',
    dataIndex: 'consultant',
    key: 'consultant',
    render: (consultant) =>
      consultant
        ? `${consultant.firstName ?? ''} ${consultant.lastName ?? ''}`.trim()
        : 'N/A',
  };

  const additionalFeedbackColumn: ColumnsType<ReviewRow>[number] = {
    title: 'Additional Feedback',
    dataIndex: 'review',
    key: 'additionalFeedback',
    width: '20%',
    render: (review: ReviewItem[]) => (
      <Typography.Text>{review?.[0]?.comment ?? '-'}</Typography.Text>
    ),
  };

  const buildQuestionColumn = (
    label: string,
    index: number,
  ): ColumnsType<ReviewRow>[number] => ({
    title: renderQuestionTitle(label),
    key: label,
    render: (_: any, record: ReviewRow) => {
      const ratings = record?.review?.[0]?.ratings;
      const value = ratings?.[index]?.rating;
      return (
        <Typography.Text>
          {typeof value === 'number' ? value : '-'}
        </Typography.Text>
      );
    },
  });

  const rateColumn: ColumnsType<ReviewRow>[number] = {
    title: 'Rate',
    key: 'rate',
    render: (_: any, record: ReviewRow) => (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ marginRight: 8 }}>
          {record?.review?.[0]?.rating ?? '0'}
        </span>
        <SvgIcon type="goldStar" width={18} height={18} />
      </div>
    ),
  };

  const actionColumn: ColumnsType<ReviewRow>[number] = {
    title: 'Action',
    dataIndex: 'actions',
    key: 'actions',
    render: (_: any, record: ReviewRow) => {
      const menu = {
        items:
          canEditReview(record)
            ? [
                {
                  key: 'view',
                  label: 'View',
                  onClick: () => handleModalOpen(record as any, 'view'),
                },
                {
                  key: 'edit',
                  label: 'Edit',
                  onClick: () => handleModalOpen(record as any, 'edit'),
                },
              ]
            : [
                {
                  key: 'view',
                  label: 'View',
                  onClick: () => handleModalOpen(record as any, 'view'),
                },
              ],
      };

      return (
        <Dropdown menu={menu}>
          <Button type="link">
            <Space>
              <SvgIcon type="threeDots" width={18} height={18} />
            </Space>
          </Button>
        </Dropdown>
      );
    },
  };

  const franchisorsColumns: ColumnsType<ReviewRow> = [
    idColumn,
    dateColumn,
    consultantColumn,
    franchisorColumn,
    statusColumn,
    buildQuestionColumn('Q1', 0),
    buildQuestionColumn('Q2', 1),
    buildQuestionColumn('Q3', 2),
    additionalFeedbackColumn,
  ];

  const consultantsColumns: ColumnsType<ReviewRow> = [
    idColumn,
    dateColumn,
    franchisorColumn,
    consultantColumn,
    statusColumn,
    rateColumn,
    additionalFeedbackColumn,
    actionColumn,
  ];

  const columns =
    activeTab === 'consultants' ? consultantsColumns : franchisorsColumns;

  // Sub-header per Figma: shows the row's directional relationship
  // (who is reviewing whom) — Consultant-to-Franchisor when looking at
  // the Franchisors tab, Franchisor-to-Consultant otherwise.
  const subheader =
    activeTab === 'consultants'
      ? 'Franchisor-to-Consultant'
      : 'Consultant-to-Franchisor';

  return (
    <div className={'review-page'}>
      <div className={'rating-management-header'}>
        <Typography.Title level={2} style={{ margin: 0 }}>
          Rating Management
        </Typography.Title>
        <Button
          type={'primary'}
          className={'rating-management-download'}
          onClick={handleDownloadTable}
          loading={isDownloading}
          disabled={isDownloading}
        >
          Download Table
        </Button>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        className={'rating-management-tabs'}
        items={[
          { key: 'franchisors', label: 'Franchisors' },
          { key: 'consultants', label: 'Consultants' },
        ]}
      />

      <div className={'rating-management-subheader'}>{subheader}</div>

      <Reviews
        reviews={data?.sessions as any[]}
        total={data?.total || 0}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
        downloadData={handleDownloadTable}
        downloadLoading={isDownloading}
        columns={columns}
        handleModalOpen={handleModalOpen}
        label={'Rating Management'}
        customFilters={
          <DateRangeFilter
            label="Date"
            fromValue={searchParams.get('startDate') || ''}
            toValue={searchParams.get('endDate') || ''}
            onChange={handleDateRangeChange}
          />
        }
      />

      {selectedBooking && (
        <ReviewModal
          open={isModalOpen}
          onClose={handleModalClose}
          booking={selectedBooking}
          isAdminView={true}
          type={isViewOrEdit}
        />
      )}
    </div>
  );
}
